import type { OpportunityPayload } from "@/lib/opportunityPayload";
import type { OperatorOpportunityUrgency } from "@/lib/operatorIntake";

export type OperatorTriagePriority = "p0" | "p1" | "p2" | "p3";

export interface OperatorTriagePacket {
  queue: "app_rescue_sprint";
  priority: OperatorTriagePriority;
  blocker_category: string;
  blocker_signals: string[];
  recommended_next_action: string;
  sla: {
    first_response_due: string;
    diagnostic_review_due: string;
  };
  follow_up: {
    subject: string;
    call_to_action: string;
    missing_context_prompt: string;
  };
  outcome_tracking: {
    seven_day_goal: string;
    statuses: string[];
  };
}

const APP_RESCUE_STATUSES = [
  "new",
  "needs_artifact",
  "diagnostic_scheduled",
  "proposal_sent",
  "won",
  "lost",
  "nurture",
];

export function buildOperatorTriagePacket(
  payload: OpportunityPayload,
  urgency: OperatorOpportunityUrgency
): OperatorTriagePacket | undefined {
  if (payload.opportunityType !== "APP_RESCUE" || payload.details.signupType === "offer_interest") {
    return undefined;
  }

  const text = triageText(payload);
  const blockerCategory = classifyAppRescueBlocker(text);
  const blockerSignals = collectAppRescueSignals(text, payload);
  const priority = priorityFor(urgency, text, payload.qualification.score);

  return {
    queue: "app_rescue_sprint",
    priority,
    blocker_category: blockerCategory,
    blocker_signals: blockerSignals,
    recommended_next_action: recommendedAction(blockerCategory, blockerSignals),
    sla: {
      first_response_due: firstResponseDueFor(priority),
      diagnostic_review_due: diagnosticReviewDueFor(priority),
    },
    follow_up: {
      subject: "App Rescue Sprint: next step",
      call_to_action: "Share a safe preview, walkthrough, or read-only repo link.",
      missing_context_prompt: "No passwords in email.",
    },
    outcome_tracking: {
      seven_day_goal: "Book a qualified diagnostic call or mark nurture/lost with a reason.",
      statuses: APP_RESCUE_STATUSES,
    },
  };
}

function triageText(payload: OpportunityPayload): string {
  return [
    payload.request.summary,
    payload.request.desiredOutcome,
    payload.request.painPoints.join(" "),
    payload.details.appUrl,
    payload.details.currentStack,
    payload.details.brokenOrUnfinished,
    payload.business.currentTools.join(" "),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function classifyAppRescueBlocker(text: string): string {
  if (/\b(stripe|payment|checkout|invoice|subscription|billing|webhook)\b/.test(text)) return "payments";
  if (/\b(auth|login|sign[- ]?in|signup|password|account|permission|role)\b/.test(text)) return "authentication_or_access";
  if (/\b(data|database|supabase|firebase|record|sync|missing|duplicate|migration)\b/.test(text)) return "data_integrity";
  if (/\b(vercel|netlify|aws|deploy|deployment|hosting|domain|dns|cloudfront|environment)\b/.test(text)) return "deployment_or_hosting";
  if (/\b(scope|requirements|features|mvp|roadmap|what to build|not sure)\b/.test(text)) return "scope_and_launch_decision";
  if (/\b(ux|ui|mobile|responsive|confusing|unfinished|flow|onboarding|screen)\b/.test(text)) return "product_ux_completion";
  return "launch_confidence_unknown";
}

function collectAppRescueSignals(text: string, payload: OpportunityPayload): string[] {
  const signals = new Set<string>();

  if (payload.details.appUrl?.trim()) signals.add("artifact_shared");
  if (payload.details.currentStack?.trim()) signals.add("stack_shared");
  if (payload.details.brokenOrUnfinished?.trim()) signals.add("blocker_described");
  if (payload.request.timeline) signals.add("timeline_shared");
  if (payload.request.budgetRange) signals.add("budget_signal_shared");
  if (/\b(customer|client|user|staff|operations|daily|production|live)\b/.test(text)) signals.add("real_user_or_ops_impact");
  if (/\b(lovable|replit|bubble|flutterflow|webflow|no-code|nocode)\b/.test(text)) signals.add("no_code_or_ai_builder_context");
  if (/\b(repo|github|codebase|pull request|branch)\b/.test(text)) signals.add("repo_context_available");

  return [...signals];
}

function priorityFor(
  urgency: OperatorOpportunityUrgency,
  text: string,
  score: number | null
): OperatorTriagePriority {
  if (urgency === "critical" || /\b(customer|client|payment|production|daily operations|down|broken live)\b/.test(text)) return "p0";
  if (urgency === "high" || (score ?? 0) >= 70) return "p1";
  if (urgency === "medium" || (score ?? 0) >= 45) return "p2";
  return "p3";
}

function firstResponseDueFor(priority: OperatorTriagePriority): string {
  if (priority === "p0") return "same business day";
  if (priority === "p1") return "within 1 business day";
  if (priority === "p2") return "within 2 business days";
  return "within 5 business days";
}

function diagnosticReviewDueFor(priority: OperatorTriagePriority): string {
  if (priority === "p0") return "within 1 business day after artifact access";
  if (priority === "p1") return "within 2 business days after artifact access";
  if (priority === "p2") return "within 3 business days after artifact access";
  return "after fit is confirmed";
}

function recommendedAction(blockerCategory: string, blockerSignals: string[]): string {
  if (!blockerSignals.includes("artifact_shared")) {
    return "Ask for a safe app, repo, preview, or walkthrough link before scoping work.";
  }
  if (blockerCategory === "payments" || blockerCategory === "authentication_or_access") {
    return "Schedule a diagnostic call and inspect the critical path before proposing feature work.";
  }
  if (blockerCategory === "scope_and_launch_decision") {
    return "Run a launch-decision review: separate blockers from wishlist work.";
  }
  return "Inspect artifact, classify blockers, and recommend a bounded rescue sprint.";
}
